import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import clsx from 'clsx';
import {
  CardBody,
  CardFooter,
  CardHeader,
  Card as ReactstrapCard,
  type CardProps as ReactstrapCardProps
} from 'reactstrap';
import BaseHeading from 'atom-components/heading';
import styles from './index.module.scss';

type CardProps = {
  className?: string;
  children: ReactNode;
} & ReactstrapCardProps;

function Card({ className, children, ...props }: CardProps): ReactElement {
  return (
    <ReactstrapCard className={clsx(styles.card, className)} {...props}>
      {children}
    </ReactstrapCard>
  );
}

type HeaderProps = {
  children: ReactNode;
};

function Header({ children }: HeaderProps): ReactElement {
  return <CardHeader>{children}</CardHeader>;
}

type HeadingProps = {
  level: 3 | 4;
  children: ReactNode;
};

/**
 * Makes all card headings should look the same, regardless of heading level
 * used. For example, an H3 card heading would used the same styling as an H4
 * card heading.
 */
function Heading({ level, children }: HeadingProps): ReactElement {
  // eslint-disable-next-line default-case
  switch (level) {
    case 3:
      return (
        <BaseHeading level={3} className="fw-bold">
          {children}
        </BaseHeading>
      );
    case 4:
      return (
        <BaseHeading level={4} className="h5 fw-bold">
          {children}
        </BaseHeading>
      );
  }
}

type BodyProps = {
  children: ReactNode;
  className?: string;
};

function Body({ className, children }: BodyProps): ReactElement {
  return <CardBody className={className}>{children}</CardBody>;
}

type FooterProps = {
  children: ReactNode;
};

function Footer({ children }: FooterProps): ReactElement {
  return <CardFooter>{children}</CardFooter>;
}

export default Object.assign(Card, {
  Header,
  Heading,
  Body,
  Footer
});
